"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.deprecatedLayoutRuleKeys = exports.LayoutRules = exports.DynamicAutoCompleteSources = exports.SystemPcbLayoutRulesValues = exports.layoutRuleTypes = void 0;
exports.hasSomeLayoutRuleConfig = hasSomeLayoutRuleConfig;
exports.hasSomeLayoutRuleConfigInMap = hasSomeLayoutRuleConfigInMap;
const auto_layout_1 = require("../../auto_layout");
const bakedRule_1 = require("../bakedRule");
const footprint_1 = require("../node/footprint");
const structure_1 = require("../node/structure");
const stackup_1 = require("./stackup");
/**
 * Examples are added as comments!
 */
exports.layoutRuleTypes = [
    // true/false
    "booleanValue",
    // 15mm
    "lengthValue",
    // 15deg
    "angle",
    // 1mm 2mm 3mm 4mm
    "cornerValues",
    // 1mm 2mm 3mm
    "lengthVector",
    // 1deg 2deg 3deg
    "angleVector",
    // 1.0
    "scalar",
    // 1.0 2.0 3.0
    "scalarVector",
    // Abc
    "text",
    // deg (with dropdown)
    "stringOption",
    // model.stl (with dropdown)
    "dynamicOption",
    // Top,Mid-Layer 2,Mid-Layer 1 (with multi dropdown)
    "dynamicMultiOption",
    // standard-4-layer (with dropdown that matches with an internal table)
    "boardStackup",
    // 150um 300um 500um
    "spaceSeparatedValues",
    // 2mm via(3mm) trace(0.5mm)
    "selectorLengthsList",
];
var SystemPcbLayoutRulesValues;
(function (SystemPcbLayoutRulesValues) {
    SystemPcbLayoutRulesValues["auto"] = "auto";
})(SystemPcbLayoutRulesValues || (exports.SystemPcbLayoutRulesValues = SystemPcbLayoutRulesValues = {}));
var DynamicAutoCompleteSources;
(function (DynamicAutoCompleteSources) {
    DynamicAutoCompleteSources["assets"] = "assets";
    DynamicAutoCompleteSources["layerRule"] = "layerRule";
    DynamicAutoCompleteSources["connectedLayers"] = "connectedLayers";
    DynamicAutoCompleteSources["stackup"] = "stackup";
    DynamicAutoCompleteSources["viaType"] = "viaType";
    DynamicAutoCompleteSources["viaOptions"] = "viaOptions";
})(DynamicAutoCompleteSources || (exports.DynamicAutoCompleteSources = DynamicAutoCompleteSources = {}));
/**
 * For detail on how the node position/rotation/layer rules work together, read the explanation doc here:
 *   https://coda.io/d/Engineering-Design-Documents_d3JGWo5rzfR/Position-and-orientation-in-PCB-mode_suyzB#_luHXc
 */
exports.LayoutRules = {
    active: {
        key: "active",
        label: "Enabled",
        description: "Removes nodes and their children from the layout.",
        default: true,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: ["true", "false"],
        valueType: "booleanValue",
        // TODO: introduce rule documentation like this:
        // documentationUrl: "https://docs.flux.ai/reference/pcb-layout-rule-types#active"
    },
    unit: {
        key: "unit",
        label: "Dimension Unit",
        description: "Unit of measure for length. Any value without a unit will be measured using this unit. For example, if you set Dimension Unit to mm, and later enter 2 2 for Position, Flux will assume you mean 2 mm 2 mm.",
        default: "mm",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: ["um", "mm", "mil", "inch", "cm", "ft", "m"],
        valueType: "stringOption",
    },
    rotationUnit: {
        key: "rotationUnit",
        label: "Rotation Unit",
        description: "Unit of measure for rotations. Any rotation value without a unit will be measured using this unit. For example, if you set Rotation Unit to deg (degrees), and later enter 2 for Rotation, Flux will assume you mean 2 deg.",
        default: "deg",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: ["deg", "rad", "grad"],
        valueType: "stringOption",
    },
    gridSpacing: {
        key: "gridSpacing",
        label: "Grid Spacing",
        description: "Sets the global grid spacing. This rule can only be applied to the root object and cannot be overridden.",
        default: "1 cm",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        auto_complete_options: ["1 cm", "1 inch"],
        supportedNodeTypes: [structure_1.PcbNodeTypes.root],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    fillStitchingDensity: {
        key: "fillStitchingDensity",
        label: "Fill Stitching Density",
        exampleImageUrl: "/assets/pcb/rule_illustrations/fills_stitching_density.svg",
        description: "Sets the density of vias for copper fill stitching.",
        default: undefined,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.polygon],
        valueType: "lengthVector",
        validRange: [0, Infinity],
    },
    fillStitchingOffset: {
        key: "fillStitchingOffset",
        label: "Fill Stitching Offset",
        exampleImageUrl: "/assets/pcb/rule_illustrations/fills_stitching_offset.svg",
        description: "Sets the offset position of copper fill stitching pattern.",
        default: 0,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.polygon],
        valueType: "lengthVector",
    },
    zIsUp: {
        key: "zIsUp",
        label: "Z axis is Up",
        description: "Changes the up axis for 3D models.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/z_axis_is_up.svg",
        default: true,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: ["true", "false"],
        supportedNodeTypes: [structure_1.PcbNodeTypes.element],
        valueType: "booleanValue",
    },
    position: {
        key: "position",
        label: "Position",
        description: "Sets the location of an object. It measures from the center of the object relative to the parent.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/position.svg",
        default: 0,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        auto_complete_options: [SystemPcbLayoutRulesValues.auto],
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "lengthVector",
        overwritingRules: ["positionX", "positionY", "positionZ"],
    },
    positionX: {
        key: "positionX",
        label: "Position X",
        description: "Sets the location of an object along the X axis. It measures from the center of the object relative to the parent.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        auto_complete_options: [SystemPcbLayoutRulesValues.auto],
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "lengthValue",
    },
    positionY: {
        key: "positionY",
        label: "Position Y",
        description: "Sets the location of an object along the Y axis. It measures from the center of the object relative to the parent.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        auto_complete_options: [SystemPcbLayoutRulesValues.auto],
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "lengthValue",
    },
    positionZ: {
        key: "positionZ",
        label: "Position Z",
        description: "Sets the location of an object along the Z axis. It measures from the center of the object relative to the parent.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        auto_complete_options: [SystemPcbLayoutRulesValues.auto],
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "lengthValue",
    },
    rotation: {
        key: "rotation",
        label: "Rotation",
        description: "Rotates the object. It measures relative to the center of the object relative to the parent.",
        default: 0,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        requiresDescendantRebake: true,
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "angleVector",
        overwritingRules: ["rotationX", "rotationY", "rotationZ"],
    },
    rotationX: {
        key: "rotationX",
        label: "Rotation X",
        description: "Rotates the object along the X axis. It measures relative to the center of the object relative to the parent.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        requiresDescendantRebake: true,
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "angle",
    },
    rotationY: {
        key: "rotationY",
        label: "Rotation Y",
        description: "Rotates the object along the Y axis. It measures relative to the center of the object relative to the parent.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        requiresDescendantRebake: true,
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "angle",
    },
    rotationZ: {
        key: "rotationZ",
        label: "Rotation Z",
        description: "Rotates the object along the Z axis. It measures relative to the center of the object relative to the parent.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        requiresDescendantRebake: true,
        unsupportedNodeTypes: [structure_1.PcbNodeTypes.root, structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.container, structure_1.PcbNodeTypes.fill],
        valueType: "angle",
    },
    size: {
        key: "size",
        label: "Size",
        description: "Sets the size of the object.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/size.svg",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthVector",
        overwritingRules: ["sizeX", "sizeY", "sizeZ"],
        minRules: ["minSize"],
        maxRules: ["maxSize"],
        validRange: [0, Infinity],
    },
    sizeX: {
        key: "sizeX",
        label: "Size X",
        description: "Sets the size of the object along the X axis.",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthValue",
        minRules: ["minSizeX"],
        maxRules: ["maxSizeX"],
        validRange: [0, Infinity],
    },
    sizeY: {
        key: "sizeY",
        label: "Size Y",
        description: "Sets the size of the object along the Y axis.",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthValue",
        minRules: ["minSizeY"],
        maxRules: ["maxSizeY"],
        validRange: [0, Infinity],
    },
    sizeZ: {
        key: "sizeZ",
        label: "Size Z",
        description: "Sets the size of the object along the Z axis.",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout],
        valueType: "lengthValue",
        minRules: ["minSizeZ"],
        maxRules: ["maxSizeZ"],
        validRange: [0, Infinity],
    },
    minSize: {
        key: "minSize",
        label: "Size Minimum",
        description: "Prevents the size of this object from being smaller than this value. Useful for defining tolerances.",
        default: 0,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthVector",
        overwritingRules: ["minSizeX", "minSizeY", "minSizeZ"],
        validRange: [0, Infinity],
    },
    minSizeX: {
        key: "minSizeX",
        label: "Size Minimum X",
        description: "Prevents the size of this object - along the X axis only - from being smaller than this value. Useful for defining tolerances.",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    minSizeY: {
        key: "minSizeY",
        label: "Size Minimum Y",
        description: "Prevents the size of this object - along the Y axis only - from being smaller than this value. Useful for defining tolerances.",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    minSizeZ: {
        key: "minSizeZ",
        label: "Size Minimum Z",
        description: "Prevents the size of this object - along the Z axis only - from being smaller than this value. Useful for defining tolerances.",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxSize: {
        key: "maxSize",
        label: "Size Maximum",
        description: "Prevents the size of this object from being larger than this value. Useful for defining tolerances.",
        // QUESTION: is it really a good idea to have non-finite values in the
        // rules? They are not JSON serializable, and will be converted silently to
        // null by JSON.stringify.
        default: Infinity,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthVector",
        overwritingRules: ["maxSizeX", "maxSizeY", "maxSizeZ"],
        validRange: [0, Infinity],
    },
    maxSizeX: {
        key: "maxSizeX",
        label: "Size Maximum X",
        description: "Prevents the size of this object - along the X axis only - from being larger than this value. Useful for defining tolerances.",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxSizeY: {
        key: "maxSizeY",
        label: "Size Maximum Y",
        description: "Prevents the size of this object - along the Y axis only - from being larger than this value. Useful for defining tolerances.",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxSizeZ: {
        key: "maxSizeZ",
        label: "Size Maximum Z",
        description: "Prevents the size of this object - along the Z axis only - from being larger than this value. Useful for defining tolerances.",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    scale: {
        key: "scale",
        label: "Scale",
        description: "Defines the scaling factor for the object.",
        default: "1",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.model,
            structure_1.PcbNodeTypes.text,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.line,
            structure_1.PcbNodeTypes.rectangle,
        ],
        valueType: "scalarVector",
        overwritingRules: ["scaleX", "scaleY", "scaleZ"],
        validRange: [0, Infinity],
    },
    scaleX: {
        key: "scaleX",
        label: "Scale X",
        description: "Defines the scaling factor for the object along the X axis.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.model,
            structure_1.PcbNodeTypes.text,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.line,
            structure_1.PcbNodeTypes.rectangle,
        ],
        valueType: "scalar",
        validRange: [0, Infinity],
    },
    scaleY: {
        key: "scaleY",
        label: "Scale Y",
        description: "Defines the scaling factor for the object along the X axis.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.model,
            structure_1.PcbNodeTypes.text,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.line,
            structure_1.PcbNodeTypes.rectangle,
        ],
        valueType: "scalar",
        validRange: [0, Infinity],
    },
    scaleZ: {
        key: "scaleZ",
        label: "Scale Z",
        description: "Defines the scaling factor for the 3D model along the Z axis.",
        allChildNodesWillInheritThisRule: false,
        affectsDescendantCalculations: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.model],
        valueType: "scalar",
        validRange: [0, Infinity],
    },
    boardInsetMargin: {
        key: "boardInsetMargin",
        label: "Board Inset Margin",
        exampleImageUrl: "/assets/pcb/rule_illustrations/fills_spacing_from_layout_edge.svg",
        description: `Defines the distance that fills will be inset from the edge of the layout.`,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout],
        valueType: "lengthValue",
    },
    keepOut: {
        key: "keepOut",
        label: "Keep Out",
        exampleImageUrl: "/assets/pcb/rule_illustrations/spacing.svg",
        description: `Prevents other objects from getting closer than the defined distance outside the the bounds of this object. Other tools call this things like "spacing", “courtyards”, or “clearance". You can be specific about the distance that certain types of objects must keep out. Entering "0.15mm" as a value will keep all objects 0.15mm away. However, entering "0.15mm via(0.5mm) pad(2mm)" will keep all vias 0.5mm away and all pads 2mm away, while every other object type will stay away by 0.15mm.`,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        requiresNetDescendantRebake: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.element,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "selectorLengthsList",
        documentationUrl: "https://docs.flux.ai/reference/layout-rules-types#keepout",
    },
    minKeepOut: {
        key: "minKeepOut",
        label: "Keep Out Minimum",
        description: "Prevents the Keep Out rule of this object from being smaller than this value. Useful for defining tolerances.",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        requiresNetDescendantRebake: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.element,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "selectorLengthsList",
    },
    maxKeepOut: {
        key: "maxKeepOut",
        label: "Keep Out Maximum",
        description: "Prevents the Keep Out rule of this object from being larger than this value. Useful for defining tolerances.",
        // QUESTION: is it really a good idea to have non-finite values in the
        // rules? They are not JSON serializable, and will be converted silently to
        // null by JSON.stringify.
        default: Infinity,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        requiresNetDescendantRebake: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.element,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "selectorLengthsList",
    },
    keepIn: {
        key: "keepIn",
        label: "Keep In",
        description: `Prevents other objects from getting further than the defined distance outside the the bounds of this object.`,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        requiresNetDescendantRebake: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.element,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.net,
        ],
        valueType: "selectorLengthsList",
        documentationUrl: "https://docs.flux.ai/reference/layout-rules-types#keepin",
    },
    minKeepIn: {
        key: "minKeepIn",
        label: "Keep In Minimum",
        description: "Prevents the Keep In rule of this object from being smaller than this value. Useful for defining tolerances.",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        requiresNetDescendantRebake: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.element,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.net,
        ],
        valueType: "selectorLengthsList",
    },
    maxKeepIn: {
        key: "maxKeepIn",
        label: "Keep In Maximum",
        description: "Prevents the Keep In rule of this object from being larger than this value. Useful for defining tolerances.",
        // QUESTION: is it really a good idea to have non-finite values in the
        // rules? They are not JSON serializable, and will be converted silently to
        // null by JSON.stringify.
        default: Infinity,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        requiresNetDescendantRebake: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.element,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.net,
        ],
        valueType: "selectorLengthsList",
    },
    autoLayoutGravity: {
        key: "autoLayoutGravity",
        label: "Auto Layout Gravity",
        description: "Defines how close elements are laid out when initially positioned in the PCB editor. Accepts values from 0 to 1, with 0 putting elements close together and 1 putting elements farther apart.",
        default: 0.5,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout],
        valueType: "scalar",
        validRange: [0, 1],
    },
    cornerRadius: {
        key: "cornerRadius",
        label: "Corner Radius",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius.svg",
        description: "Adds rounding to the corners of an object.",
        default: 0,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.rectangle,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "cornerValues",
        validRange: [0, Infinity], // Also clamped dynamically in the code, but specifying a fixed range here too for clarity.
        overwritingRules: [
            "cornerRadiusTopLeft",
            "cornerRadiusTopRight",
            "cornerRadiusBottomRight",
            "cornerRadiusBottomLeft",
        ],
        minRules: ["minCornerRadius"],
        maxRules: ["maxCornerRadius"],
    },
    minCornerRadius: {
        key: "minCornerRadius",
        label: "Corner Radius Minimum",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_minimum.svg",
        description: "Prevents the corner radius of an object from being smaller than this value.",
        default: 0,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        valueType: "cornerValues",
        overwritingRules: [
            "minCornerRadiusTopLeft",
            "minCornerRadiusTopRight",
            "minCornerRadiusBottomRight",
            "minCornerRadiusBottomLeft",
        ],
    },
    maxCornerRadius: {
        key: "maxCornerRadius",
        label: "Corner Radius Maximum",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_maximum.svg",
        description: "Prevents the corner radius on an object from being larger than this value.",
        default: undefined,
        setByDefault: false,
        allChildNodesWillInheritThisRule: false,
        valueType: "cornerValues",
        overwritingRules: [
            "maxCornerRadiusTopLeft",
            "maxCornerRadiusTopRight",
            "maxCornerRadiusBottomRight",
            "maxCornerRadiusBottomLeft",
        ],
    },
    cornerRadiusTopLeft: {
        key: "cornerRadiusTopLeft",
        label: "Corner Radius Top Left",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_top_left.svg",
        description: "Adds rounding to the top left corner on an object.",
        default: undefined,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad, structure_1.PcbNodeTypes.zone, structure_1.PcbNodeTypes.layout, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity], // Also clamped dynamically in the code, but specifying a fixed range here too for clarity.
    },
    cornerRadiusTopRight: {
        key: "cornerRadiusTopRight",
        label: "Corner Radius Top Right",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_top_right.svg",
        description: "Adds rounding to the top right corner on an object.",
        default: undefined,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad, structure_1.PcbNodeTypes.zone, structure_1.PcbNodeTypes.layout, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity], // Also clamped dynamically in the code, but specifying a fixed range here too for clarity.
    },
    cornerRadiusBottomRight: {
        key: "cornerRadiusBottomRight",
        label: "Corner Radius Bottom Right",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_bottom_right.svg",
        description: "Adds rounding to the bottom right corner on an object.",
        default: undefined,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad, structure_1.PcbNodeTypes.zone, structure_1.PcbNodeTypes.layout, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity], // Also clamped dynamically in the code, but specifying a fixed range here too for clarity.
    },
    cornerRadiusBottomLeft: {
        key: "cornerRadiusBottomLeft",
        label: "Corner Radius Bottom Left",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_bottom_left.svg",
        description: "Adds rounding to the bottom left corner on an object.",
        default: undefined,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad, structure_1.PcbNodeTypes.zone, structure_1.PcbNodeTypes.layout, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity], // Also clamped dynamically in the code, but specifying a fixed range here too for clarity.
    },
    minCornerRadiusTopLeft: {
        key: "minCornerRadiusTopLeft",
        label: "Corner Radius Minimum Top Left",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_top_left_minimum.svg",
        description: "Prevents the top left corner radius of an object from being smaller than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    minCornerRadiusTopRight: {
        key: "minCornerRadiusTopRight",
        label: "Corner Radius Minimum Top Right",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_top_right_minimum.svg",
        description: "Prevents the top right corner radius of an object from being smaller than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    minCornerRadiusBottomRight: {
        key: "minCornerRadiusBottomRight",
        label: "Corner Radius Minimum Bottom Right",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_bottom_right_minimum.svg",
        description: "Prevents the bottom right corner radius of an object from being smaller than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    minCornerRadiusBottomLeft: {
        key: "minCornerRadiusBottomLeft",
        label: "Corner Radius Minimum Bottom Left",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_bottom_left_minimum.svg",
        description: "Prevents the bottom left corner radius of an object from being smaller than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    maxCornerRadiusTopLeft: {
        key: "maxCornerRadiusTopLeft",
        label: "Corner Radius Maximum Top Left",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_top_left_maximum.svg",
        description: "Prevents the top left corner radius of an object from being larger than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    maxCornerRadiusTopRight: {
        key: "maxCornerRadiusTopRight",
        label: "Corner Radius Maximum Top Right",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_top_right_maximum.svg",
        description: "Prevents the top right corner radius of an object from being larger than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    maxCornerRadiusBottomRight: {
        key: "maxCornerRadiusBottomRight",
        label: "Corner Radius Maximum Bottom Right",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_bottom_right_maximum.svg",
        description: "Prevents the bottom right corner radius of an object from being larger than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    maxCornerRadiusBottomLeft: {
        key: "maxCornerRadiusBottomLeft",
        label: "Corner Radius Maximum Bottom Left",
        exampleImageUrl: "/assets/pcb/rule_illustrations/corner_radius_bottom_left_maximum.svg",
        description: "Prevents the bottom left corner radius of an object from being larger than this value.",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    zoneShape: {
        key: "zoneShape",
        label: "Zone Shape",
        description: "Defines the shape of a zone, either circular or rectangular.",
        default: footprint_1.FootPrintPadShape.rectangle,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.PcbLayoutFootprintPadShape.rectangle, bakedRule_1.PcbLayoutFootprintPadShape.circular],
        supportedNodeTypes: [structure_1.PcbNodeTypes.zone],
        valueType: "stringOption",
    },
    polygonShape: {
        key: "polygonShape",
        label: "Polygon Shape",
        description: "Defines the shape of a zone, either circular or rectangular. Can also accept a custom shape using the SVG path syntax.",
        default: footprint_1.FootPrintPadShape.rectangle,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.PcbLayoutFootprintPadShape.rectangle, bakedRule_1.PcbLayoutFootprintPadShape.circular],
        supportedNodeTypes: [structure_1.PcbNodeTypes.polygon],
        valueType: "stringOption",
    },
    padShape: {
        key: "padShape",
        label: "Pad Shape",
        exampleImageUrl: "/assets/pcb/rule_illustrations/pad_shape.svg",
        description: "Defines the shape of a pad, either circular or rectangular.",
        default: footprint_1.FootPrintPadShape.circular,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.PcbLayoutFootprintPadShape.rectangle, bakedRule_1.PcbLayoutFootprintPadShape.circular],
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad],
        valueType: "stringOption",
    },
    padHoleType: {
        key: "padHoleType",
        label: "Pad Type",
        exampleImageUrl: "/assets/pcb/rule_illustrations/pad_type.svg",
        description: "Defines the type of a pad. SMD: a surface mount pad with no drill hole. HOLE: a drill hole with no copper. STD: a through hole pad. CONN: a connector pad with no drill hole, often used at the edges of a board.",
        default: bakedRule_1.FootPrintPadHoleType.SurfaceMountDevice,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [
            bakedRule_1.FootPrintPadHoleType.SurfaceMountDevice,
            bakedRule_1.FootPrintPadHoleType.nonPlatedHole,
            bakedRule_1.FootPrintPadHoleType.platedThroughHole,
            bakedRule_1.FootPrintPadHoleType.testPinOrCardEdgeConnector,
        ],
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad, structure_1.PcbNodeTypes.zone],
        valueType: "stringOption",
    },
    viaType: {
        key: "viaType",
        label: "Via Type",
        description: "Defines the type of a via. A through hole via is a plated drill hole with annular rings on ever layer. A blind or buried via is a plated drill hole from a specific layer to another with annular rings on those layers. A micro-via is a conical hole bored with a laser from one layer to an adjacent layer.",
        default: bakedRule_1.PcbViaType.throughHole,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.PcbViaType.throughHole],
        supportedNodeTypes: [structure_1.PcbNodeTypes.via],
        valueType: "stringOption",
    },
    viaOptions: {
        key: "viaOptions",
        label: "Via Options",
        description: "Defines the set of via configurations from the stackup editor that will be used to create the connection between the 'Connected layers' of a Smart Via.",
        default: "All",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        requiresDescendantRebake: true,
        dynamic_auto_complete_source: DynamicAutoCompleteSources.viaOptions,
        supportedNodeTypes: [structure_1.PcbNodeTypes.smartVia],
        valueType: "dynamicMultiOption",
    },
    thermalRelief: {
        key: "thermalRelief",
        label: "Thermal Relief",
        description: `Defines how a pad connects to a fill. The Relief Connect option separates the pad from the fill and connects them with conductors. The distance of the separation is defined by the Keep Out rule. Useful when soldering a component to a pad. The Direct Connect option is essentially "no thermal reliefs". It removes the conductors and prevents Keep Outs from having an effect, allowing the pad and the fill to connect directly.`,
        exampleImageUrl: "/assets/pcb/rule_illustrations/thermal_relief.svg",
        default: bakedRule_1.PcbViaThermalReliefTypes.directConnect,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.PcbViaThermalReliefTypes.reliefConnect, bakedRule_1.PcbViaThermalReliefTypes.directConnect],
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "stringOption",
    },
    thermalReliefConductors: {
        key: "thermalReliefConductors",
        label: "Thermal Relief Conductors",
        description: "Defines how many conductors are used for a thermal relief.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/thermal_relief_conductors.svg",
        default: bakedRule_1.PcbViaThermalReliefConductors.four,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.PcbViaThermalReliefConductors.two, bakedRule_1.PcbViaThermalReliefConductors.four],
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "stringOption",
    },
    thermalReliefConductorWidth: {
        key: "thermalReliefConductorWidth",
        label: "Thermal Relief Conductor Width",
        description: "Defines the width of the conductors used for a thermal relief.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/thermal_relief_conductor_width.svg",
        default: "15mil",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    thermalReliefConductorAngle: {
        key: "thermalReliefConductorAngle",
        label: "Thermal Relief Conductor Angle",
        description: "Defines the angle of the conductors used for a thermal relief.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/thermal_relief_conductor_angle.svg",
        default: bakedRule_1.PcbViaThermalReliefConductorAngle.nintyDegree,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [
            bakedRule_1.PcbViaThermalReliefConductorAngle.nintyDegree,
            bakedRule_1.PcbViaThermalReliefConductorAngle.fourtyFiveDegree,
        ],
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "angle",
    },
    solderPasteMaskExpansion: {
        key: "solderPasteMaskExpansion",
        label: "Solder Paste Mask Expansion",
        description: "Allows the edge of the solder paste mask to expand beyond or contract within the edge of a pad or via. A positive value will expand the edge, a negative value will contract the edge, and a value of 0 will match the edge to the pad or via.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/solder_paste_mask_expansion.svg",
        default: undefined,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
    },
    solderMaskExpansion: {
        key: "solderMaskExpansion",
        label: "Solder Mask Expansion",
        description: "Allows the edge of the solder mask to expand beyond or contract within the edge of a pad or via. A positive value will expand the edge, a negative value will contract the edge, and a value of 0 will match the edge to the pad or via.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/solder_mask_expansion.svg",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
    },
    solderMaskTopExpansion: {
        key: "solderMaskTopExpansion",
        label: "Solder Mask Expansion Top",
        description: "Allows the edge of the solder mask - on the Top layer only - to expand beyond or contract within the edge of a pad or via. A positive value will expand the edge, a negative value will contract the edge, and a value of 0 will match the edge to the pad or via.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/solder_mask_top_expansion.svg",
        default: undefined,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
    },
    solderMaskBottomExpansion: {
        key: "solderMaskBottomExpansion",
        label: "Solder Mask Expansion Bottom",
        description: "Allows the edge of the solder mask - on the Bottom layer only - to expand beyond or contract within the edge of a pad or via. A positive value will expand the edge, a negative value will contract the edge, and a value of 0 will match the edge to the pad or via.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/solder_mask_bottom_expansion.svg",
        default: undefined,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
    },
    solderMaskExpansionFromTheHoleEdge: {
        key: "solderMaskExpansionFromTheHoleEdge",
        label: "Solder Mask Expansion From Hole Edge",
        description: "Allows the edge of the solder mask to expand beyond or contract within the edge of a drill hole. A positive value will expand the edge, a negative value will reduce the edge, and a value of 0 will match the edge to the drill hole.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/solder_mask_expansion_from_hole_edge.svg",
        default: undefined,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
    },
    connectedLayers: {
        key: "connectedLayers",
        label: "Connected Layers",
        exampleImageUrl: "/assets/pcb/rule_illustrations/connected_layers.svg",
        description: "Defines which layers an object connects to. Useful for copper fills, vias or STD pads to set which layers have annular rings (or are connected to a fill) and which layers to skip.",
        default: undefined,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        // when connectedLayer changes for SmartVia, new children are created, and they need to be baked
        // NOTE: We actually only want to rebake descendant for SmartVia, but there's no good way to set
        // this flag conditionally based on node type...
        requiresDescendantRebake: true,
        dynamic_auto_complete_source: DynamicAutoCompleteSources.connectedLayers,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.polygon,
        ],
        valueType: "dynamicMultiOption",
    },
    holeSize: {
        key: "holeSize",
        label: "Hole Size",
        description: "Sets the size of any drill holes in this object.",
        setByDefault: true,
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthVector",
        overwritingRules: ["holeSizeX", "holeSizeY"],
        minRules: ["minHoleSize"],
        maxRules: ["maxHoleSize"],
        validRange: [0, Infinity],
    },
    holeSizeX: {
        key: "holeSizeX",
        label: "Hole Size X",
        description: "Sets the size of any drill holes in this object along the X axis.",
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
        minRules: ["minHoleSizeX"],
        maxRules: ["maxHoleSizeX"],
        validRange: [0, Infinity],
    },
    holeSizeY: {
        key: "holeSizeY",
        label: "Hole Size Y",
        description: "Sets the size of any drill holes in this object along the Y axis.",
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
        minRules: ["minHoleSizeY"],
        maxRules: ["maxHoleSizeY"],
        validRange: [0, Infinity],
    },
    minHoleSize: {
        key: "minHoleSize",
        label: "Hole Size Minimum",
        description: "Prevents the size of any drill holes in this object from being smaller than this value. Useful for tolerances.",
        setByDefault: true,
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthVector",
        overwritingRules: ["minHoleSizeX", "minHoleSizeY"],
        validRange: [0, Infinity],
    },
    minHoleSizeX: {
        key: "minHoleSizeX",
        label: "Hole Size Minimum X",
        description: "Prevents the size of any drill holes in this object - along the X axis only - from being smaller than this value.",
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    minHoleSizeY: {
        key: "minHoleSizeY",
        label: "Hole Size Minimum Y",
        description: "Prevents the size of any drill holes in this object - along the Y axis only - from being smaller than this value.",
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxHoleSize: {
        key: "maxHoleSize",
        label: "Hole Size Maximum",
        description: "Prevents the size of any drill holes in this object from being larger than this value. Useful for tolerances.",
        setByDefault: true,
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthVector",
        overwritingRules: ["maxHoleSizeX", "maxHoleSizeY"],
        validRange: [0, Infinity],
    },
    maxHoleSizeX: {
        key: "maxHoleSizeX",
        label: "Hole Size Maximum X",
        description: "Prevents the size of any drill holes in this object - along the X axis only - from being larger than this value.",
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxHoleSizeY: {
        key: "maxHoleSizeY",
        label: "Hole Size Maximum Y",
        description: "Prevents the size of any drill holes in this object - along the Y axis only - from being larger than this value.",
        auto_complete_options: ["0.2mm", "0.3mm", "0.6mm", "0.7mm"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.via, structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    holePosition: {
        key: "holePosition",
        label: "Hole Position",
        description: "Defines the location of a drill hole in this object, measuring from the center of the drill hole relative to the center of a parent object.",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad],
        overwritingRules: ["holePositionX", "holePositionY"],
        valueType: "lengthVector",
    },
    holePositionX: {
        key: "holePositionX",
        label: "Hole Position X",
        description: "Defines the location of a drill hole in this object - along the X axis only - measuring from the center of the drill hole relative to the center of a parent object.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
    },
    holePositionY: {
        key: "holePositionY",
        label: "Hole Position Y",
        description: "Defines the location of a drill hole in this object - along the Y axis only - measuring from the center of the drill hole relative to the center of a parent object.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.pad],
        valueType: "lengthValue",
    },
    boardColor: {
        key: "boardColor",
        label: "Layout Color",
        description: "Defines the color of the solder mask used during production. Preview this in 3D on PCB Mode.",
        default: "green",
        auto_complete_options: ["green", "blue", "yellow", "white", "red", "black", "purple"],
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout],
        valueType: "stringOption",
    },
    boardShape: {
        key: "boardShape",
        label: "Layout Shape",
        description: "Defines the shape of the circuit board. Supports basic shapes and SVG code for custom shapes.",
        default: bakedRule_1.PcbLayoutShape.rectangle,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.PcbLayoutShape.rectangle, bakedRule_1.PcbLayoutShape.circular, bakedRule_1.PcbLayoutShape.flux],
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout],
        valueType: "stringOption",
    },
    silkColor: {
        key: "silkColor",
        label: "Silk Color",
        description: "Defines the color of the silkscreen used during production. Preview this in 3D on PCB Mode.",
        default: "white",
        auto_complete_options: ["white", "red", "blue", "black", "yellow", "purple"],
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        valueType: "stringOption",
    },
    asset: {
        key: "asset",
        label: "Asset",
        description: "Apply a asset file to a object. Select a asset that’s been added to a component’s Asset Panel and to associate it with a supported object.",
        default: undefined,
        dynamic_auto_complete_source: DynamicAutoCompleteSources.assets,
        allChildNodesWillInheritThisRule: false,
        // when a footprint asset changes, new children are created, and they need to be baked
        requiresDescendantRebake: true,
        supportedNodeTypes: [
            structure_1.PcbNodeTypes.model,
            structure_1.PcbNodeTypes.footprint,
            structure_1.PcbNodeTypes.layout,
            structure_1.PcbNodeTypes.line,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
        ],
        valueType: "dynamicOption",
    },
    physicalMaterials: {
        key: "physicalMaterials",
        label: "Physical Materials",
        description: "Defines the level of detail used on a 3D model’s materials. If set on true, realistic materials will be used, otherwise basic materials will be used.",
        default: "true",
        setByDefault: true,
        auto_complete_options: ["true", "false"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.model],
        valueType: "booleanValue",
    },
    showEdges: {
        key: "showEdges",
        label: "Show Model Edges",
        description: "Highlights the edges of selected 3D model with a black line. An edge is where two faces of the 3D model meet.",
        default: "false",
        setByDefault: true,
        auto_complete_options: ["true", "false"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.model],
        valueType: "booleanValue",
    },
    stackup: {
        key: "stackup",
        label: "Stackup",
        exampleImageUrl: "/assets/pcb/rule_illustrations/stackup.svg",
        description: "Defines the layer stackup for a Layout. Offers a number of standard defaults.",
        setByDefault: true,
        auto_complete_options: (0, stackup_1.getAllStackupConfigLabels)(),
        dynamic_auto_complete_source: DynamicAutoCompleteSources.stackup,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.layout, structure_1.PcbNodeTypes.footprint],
        valueType: "boardStackup",
    },
    layer: {
        key: "layer",
        label: "Layer",
        description: "Defines what layer the footprint is placed. Footprints can be placed on either the top or bottom layers.",
        default: bakedRule_1.LayerOrientation.top,
        setByDefault: true,
        dynamic_auto_complete_source: DynamicAutoCompleteSources.layerRule,
        auto_complete_options: [bakedRule_1.LayerOrientation.top, bakedRule_1.LayerOrientation.bottom, bakedRule_1.LayerOrientation.invert],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [
            // Note: it's important that Layout and Container nodes aren't included in this
            // list, because that allows new nodes to be added to the current layer instead
            // of the container node's default layer.
            structure_1.PcbNodeTypes.element,
            structure_1.PcbNodeTypes.text,
            structure_1.PcbNodeTypes.line,
            structure_1.PcbNodeTypes.circle,
            structure_1.PcbNodeTypes.rectangle,
            structure_1.PcbNodeTypes.net,
            structure_1.PcbNodeTypes.routeSegment,
            structure_1.PcbNodeTypes.pad,
            structure_1.PcbNodeTypes.zone,
            structure_1.PcbNodeTypes.via,
            structure_1.PcbNodeTypes.model,
            structure_1.PcbNodeTypes.footprint,
        ],
        valueType: "dynamicOption",
    },
    content: {
        key: "content",
        label: "Content",
        description: "Sets the string that a text object displays on the PCB canvas. Overrides any default string that may be present.",
        default: undefined,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.text],
        valueType: "text",
        auto_complete_options: [
            "=this.designator",
            "=this.type",
            "=this.rules.position",
            "=this.element.designator",
            "=this.element.defaultPropertyValue",
            "=this.element.part.name",
            "=this.element.part.url",
            "=project.name",
            "=project.description",
            "=project.url",
            "=project.slug",
            "=project.owner.handle",
            "=project.owner.url",
            "=project.changeHistory.currentChangeUid",
            "=project.changeHistory.currentChangeShortUid",
        ],
    },
    fontSize: {
        key: "fontSize",
        label: "Font Size",
        description: "Sets the size of the typeface that a text object displays.",
        setByDefault: true,
        default: "1mm",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.text],
        valueType: "lengthValue",
        minRules: ["minFontSize"],
        maxRules: ["maxFontSize"],
        validRange: [0, Infinity],
    },
    minFontSize: {
        key: "minFontSize",
        label: "Font Size Minimum",
        description: "Prevents the font size of a text object from being smaller than this value. Useful for tolerances.",
        setByDefault: true,
        default: "0.8mm",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.text],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxFontSize: {
        key: "maxFontSize",
        label: "Font Size Maximum",
        description: "Prevents the font size of a text object from being larger than this value. Useful for tolerances.",
        // QUESTION: is it really a good idea to have non-finite values in the
        // rules? They are not JSON serializable, and will be converted silently to
        // null by JSON.stringify.
        default: Infinity,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.text],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    fontStyle: {
        key: "fontStyle",
        label: "Font Style",
        description: "Sets the style for the typeface displayed by a text object, such as bold or italic.",
        setByDefault: true,
        default: bakedRule_1.FontStyle.normal,
        auto_complete_options: [bakedRule_1.FontStyle.normal, bakedRule_1.FontStyle.italic],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.text],
        valueType: "stringOption",
    },
    textAlign: {
        key: "textAlign",
        label: "Text Alignment",
        description: "Sets the alignment of the type displayed by a text object. Measures relative to the center of the text object.",
        setByDefault: true,
        default: bakedRule_1.TextAlignment.left,
        auto_complete_options: [bakedRule_1.TextAlignment.left, bakedRule_1.TextAlignment.right, bakedRule_1.TextAlignment.center],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.text],
        valueType: "stringOption",
    },
    // Trace width overwrites size.x
    traceWidth: {
        key: "traceWidth",
        label: "Trace Width",
        description: "Sets the width of traces in this object.",
        default: "10mil",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        minRules: ["minTraceWidth"],
        maxRules: ["maxTraceWidth"],
    },
    minTraceWidth: {
        key: "minTraceWidth",
        label: "Trace Width Minimum",
        description: "Prevents the size of traces and copper fill features in this object from being smaller than this value.",
        default: "0.15mm",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment, structure_1.PcbNodeTypes.polygon, structure_1.PcbNodeTypes.net],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxTraceWidth: {
        key: "maxTraceWidth",
        label: "Trace Width Maximum",
        description: "Prevents the size of traces in this object from being larger than this value.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    traceShape: {
        key: "traceShape",
        label: "Trace Shape",
        description: "Defines the shape of a trace, either straight or curved (arc).",
        default: bakedRule_1.TraceShapeType.straight,
        setByDefault: false,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.TraceShapeType.straight, bakedRule_1.TraceShapeType.curve],
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "stringOption",
    },
    traceShapeStart: {
        key: "traceShapeStart",
        label: "Trace Shape Start",
        description: "Defines the shape of a trace from the start, either straight or curved (arc).",
        default: undefined,
        setByDefault: false,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.TraceShapeType.straight, bakedRule_1.TraceShapeType.curve],
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "stringOption",
    },
    traceShapeEnd: {
        key: "traceShapeEnd",
        label: "Trace Shape End",
        description: "Defines the shape of a trace from the end, either straight or curved (arc).",
        default: undefined,
        setByDefault: false,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.TraceShapeType.straight, bakedRule_1.TraceShapeType.curve],
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "stringOption",
    },
    traceCornerRadius: {
        key: "traceCornerRadius",
        label: "Trace Corner Radius",
        description: "Defines the radial size that the curved trace will try to match.",
        default: 0,
        setByDefault: false,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    traceCornerRadiusStart: {
        key: "traceCornerRadiusStart",
        label: "Trace Corner Radius Start",
        description: "Defines the radial size from the start, that the curved trace will try to match.",
        default: undefined,
        setByDefault: false,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    traceCornerRadiusEnd: {
        key: "traceCornerRadiusEnd",
        label: "Trace Corner Radius End",
        description: "Defines the radial size from the end, that the curved trace will try to match.",
        default: undefined,
        setByDefault: false,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    traceCornerRadiusMinimum: {
        key: "traceCornerRadiusMinimum",
        label: "Trace Corner Radius Minimum",
        description: "Defines the minimal radial size that the curved trace must adhere to.",
        default: "500um",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    preferredTraceWidths: {
        key: "preferredTraceWidths",
        label: "Preferred Trace Widths",
        description: "Defines a set of widths which will show up in the Context Menu for easy access. The keyboard shortcut W will cycle through the set.",
        exampleImageUrl: "/assets/pcb/rule_illustrations/preferredTraceWidths.svg",
        default: undefined,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: ["150um 300um 500um", "6mil 12mil 20mil"],
        supportedNodeTypes: [structure_1.PcbNodeTypes.routeSegment, structure_1.PcbNodeTypes.net],
        valueType: "spaceSeparatedValues",
    },
    strokeWidth: {
        key: "strokeWidth",
        label: "Stroke Width",
        description: "Sets the width of silk objects - lines, circles, and rectangles.",
        default: "0.1524mm",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [SystemPcbLayoutRulesValues.auto],
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.circle, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    minStrokeWidth: {
        key: "minStrokeWidth",
        label: "Stroke Width Minimum",
        description: "Prevents the width of silk objects - lines, circles, and rectangles from being smaller than this value.",
        default: "0.15mm",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.circle, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    maxStrokeWidth: {
        key: "maxStrokeWidth",
        label: "Stroke Width Maximum",
        description: "Prevents the width of silk objects - lines, circles, and rectangles from being larger than this value.",
        // QUESTION: is it really a good idea to have non-finite values in the
        // rules? They are not JSON serializable, and will be converted silently to
        // null by JSON.stringify.
        default: Infinity,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.circle, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    strokeStyle: {
        key: "strokeStyle",
        label: "Stroke Style",
        description: "Sets the style of silk objects - lines, circles, and rectangles, such as solid or dashed.",
        default: bakedRule_1.IPcbLineShapeStyles.solid,
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        auto_complete_options: [bakedRule_1.IPcbLineShapeStyles.solid, bakedRule_1.IPcbLineShapeStyles.dashed],
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.circle, structure_1.PcbNodeTypes.rectangle],
        valueType: "stringOption",
    },
    strokeLength: {
        key: "strokeLength",
        label: "Stroke Length",
        description: "Defines the length of dashed segments for dashed-style silk elements.",
        default: "0.4mm",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.circle, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    strokeSpacing: {
        key: "strokeSpacing",
        label: "Stroke Spacing",
        description: "Defines the spacing for dashed-style silk elements.",
        default: "0.4mm",
        setByDefault: true,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.circle, structure_1.PcbNodeTypes.rectangle],
        valueType: "lengthValue",
        validRange: [0, Infinity],
    },
    linePath: {
        key: "linePath",
        label: "Line Path",
        description: "Customizes the shape of silk lines using SVG path syntax.",
        default: undefined,
        setByDefault: false,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line],
        valueType: "text",
    },
    startPosition: {
        key: "startPosition",
        label: "Shape Start",
        description: "Sets the location of the first point that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.rectangle, structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthVector",
        overwritingRules: ["startPositionX", "startPositionY", "startPositionZ"],
    },
    startPositionX: {
        key: "startPositionX",
        label: "Shape Start X",
        description: "Sets the location of the first point - along the X axis - that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.rectangle, structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
    },
    startPositionY: {
        key: "startPositionY",
        label: "Shape Start Y",
        description: "Sets the location of the first point - along the Y axis - that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.rectangle, structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
    },
    startPositionZ: {
        key: "startPositionZ",
        label: "Shape Start Z",
        description: "Sets the location of the first point - along the Z axis - that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    endPosition: {
        key: "endPosition",
        description: "Sets the location of the final point that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        label: "Shape End",
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.rectangle, structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthVector",
        overwritingRules: ["endPositionX", "endPositionY", "endPositionZ"],
    },
    endPositionX: {
        key: "endPositionX",
        label: "Shape End X",
        description: "Sets the location of the final point - along the X axis - that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.rectangle, structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
    },
    endPositionY: {
        key: "endPositionY",
        label: "Shape End Y",
        description: "Sets the location of the final point - along the Y axis - that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.line, structure_1.PcbNodeTypes.rectangle, structure_1.PcbNodeTypes.routeSegment],
        valueType: "lengthValue",
    },
    endPositionZ: {
        key: "endPositionZ",
        description: "Sets the location of the final point - along the Z axis - that defines a line or a rectangle. For example, a square with 2mm side and centered in [0mm 0mm] would have a start position [-1mm -1mm] and an end position [1mm 1mm].",
        label: "Shape End Z",
        allChildNodesWillInheritThisRule: false,
        valueType: "lengthValue",
    },
    package: {
        key: "package",
        label: "Package",
        description: "Sets the package of a footprint. Used for parametrically generating a footprint.",
        default: undefined,
        auto_complete_options: [
            "Grid Array",
            "BGA",
            "CSP",
            "QFN",
            "LGA",
            "LQFP",
            "TQFP",
            "MSOP",
            "SSOP",
            "Chip",
            "SOIC",
            "DFN",
            "TSSOP",
            "DIP",
            "TO",
            "SOT",
        ],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "stringOption",
        requiresDescendantRebake: true,
    },
    gridArrayCount: {
        key: "gridArrayCount",
        label: "Grid Array Count",
        description: "Defines the number of items in the grid array for a footprint. Used for parametrically generating a footprint whose package is set to grid. A value of 16 16 will result in a grid of 16 across and 16 down. The Pin Number property on terminals determines placement in this grid. For example, a terminal with pin number A1 will be placed in the first grid position in the top left.",
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "scalarVector",
        validRange: [0, Infinity],
        overwritingRules: ["gridArrayCountX", "gridArrayCountY"],
        requiresDescendantRebake: true,
    },
    gridArrayCountX: {
        key: "gridArrayCountX",
        label: "Grid Array Count X",
        description: "Defines the number of items along the X axis in the grid array for a footprint. Used for parametrically generating a footprint whose package is set to grid. A value of 16 will result in a grid which is 16 across.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "scalar",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    gridArrayCountY: {
        key: "gridArrayCountY",
        label: "Grid Array Count Y",
        description: "Defines the number of items along the Y axis in the grid array for a footprint. Used for parametrically generating a footprint whose package is set to grid. A value of 16 will result in a grid which is 16 down.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "scalar",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    gridArrayRowAlphabet: {
        key: "gridArrayRowAlphabet",
        label: "Grid Array Row Alphabet",
        description: 'Defines the possible values which can be used to set the position of a pad in an array of rows. A value from this sequence is used as the first value in a coordinate pair in the Pin Number property. For example, given a Pin Number of A1, the A is from the sequence defined here.\nThe values are a space-separated sequence of letters. After the last letter is reached, the system will wrap. Meaning it will start the sequence again, but prepend the first value, ex. AA AB AC. After that, it will wrap again and prepend the second value, ex. BA BB BC, and so on. The value "numeric" of this rule will make use of numbers instead, starting from 1.\nUsed for parametrically generating a footprint whose package is set to grid.',
        default: "A B C D E F G H J K L M N P R T U V W Y",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "text",
        requiresDescendantRebake: true,
    },
    gridArrayColumnAlphabet: {
        key: "gridArrayColumnAlphabet",
        label: "Grid Array Column Alphabet",
        description: 'Defines the possible values which can be used to set the position of a pad in an array of columns. A value from this sequence is used as the second value in a coordinate pair in the Pin Number property. For example, given a Pin Number of 1A, the A is from the sequence defined here.\nThe values are a space-separated sequence of letters. After the last letter is reached, the system will wrap. Meaning it will start the sequence again, but prepend the first value, ex. AA AB AC. After that, it will wrap again and prepend the second value, ex. BA BB BC, and so on. The value "numeric" of this rule will make use of numbers instead, starting from 1.\nUsed for parametrically generating a footprint whose package is set to grid.',
        default: undefined,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "text",
        requiresDescendantRebake: true,
    },
    ballType: {
        key: "ballType",
        label: "Ball Type",
        description: "Sets the type of ball for a BGA footprint. Used for parametrically generating a footprint.",
        default: "collapsing ball",
        auto_complete_options: ["collapsing ball", "non-collapsing ball"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "stringOption",
        requiresDescendantRebake: true,
    },
    pinPitch: {
        key: "pinPitch",
        label: "Pin Pitch",
        description: "Sets the pitch of pads on the footprint and pins on the package. Used for parametrically generating a footprint.",
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthVector",
        validRange: [0, Infinity],
        overwritingRules: ["pinPitchX", "pinPitchY"],
        requiresDescendantRebake: true,
    },
    pinPitchX: {
        key: "pinPitchX",
        label: "Pin Pitch X",
        description: "Sets the pitch of pads on the footprint and pins on the package along the X axis. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    pinPitchY: {
        key: "pinPitchY",
        label: "Pin Pitch Y",
        description: "Sets the pitch of pads on the footprint and pins on the package along the Y axis. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    pinSize: {
        key: "pinSize",
        label: "Pin Size",
        description: "Sets the size of the pads on the footprint and the pins on the package. Used for parametrically generating a footprint.",
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthVector",
        validRange: [0, Infinity],
        overwritingRules: ["pinSizeX", "pinSizeY", "pinSizeZ"],
        requiresDescendantRebake: true,
    },
    pinSizeX: {
        key: "pinSizeX",
        label: "Pin Size X",
        description: "Sets the size of the pads on the footprint and the pins on the package along the X axis. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    pinSizeY: {
        key: "pinSizeY",
        label: "Pin Size Y",
        description: "Sets the size of the pads on the footprint and the pins on the package along the Y axis. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    pinSizeZ: {
        key: "pinSizeZ",
        label: "Pin Size Z",
        description: "Sets the size of pins along the Z axis for a footprint. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packageSize: {
        key: "packageSize",
        label: "Package Size",
        description: "Sets the size of the package and silk outline for a footprint. Used for parametrically generating a footprint.",
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthVector",
        validRange: [0, Infinity],
        overwritingRules: ["packageSizeX", "packageSizeY", "packageSizeZ"],
        requiresDescendantRebake: true,
    },
    packageSizeX: {
        key: "packageSizeX",
        label: "Package Size X",
        description: "Sets the size of the package and silk outline along the X axis for a footprint. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packageSizeY: {
        key: "packageSizeY",
        label: "Package Size Y",
        description: "Sets the size of the package and silk outline along the Y axis for a footprint. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packageSizeZ: {
        key: "packageSizeZ",
        label: "Package Size Z",
        description: "Sets the size of the package along the Z axis for a footprint. Used for parametrically generating a footprint.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packagePlacementTolerance: {
        key: "packagePlacementTolerance",
        label: "Package Placement Tolerance",
        description: "Sets the tolerance for placing a package, which also affects the silk outline. Used for parametrically generating footprints.",
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint],
        valueType: "lengthVector",
        validRange: [0, Infinity],
        overwritingRules: ["packagePlacementToleranceX", "packagePlacementToleranceY"],
        requiresDescendantRebake: true,
    },
    packagePlacementToleranceX: {
        key: "packagePlacementToleranceX",
        label: "Package Placement Tolerance X",
        description: "Sets the tolerance along the X axis for placing a package, which also affects the silk outline. Used for parametrically generating footprints.",
        allChildNodesWillInheritThisRule: true,
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packagePlacementToleranceY: {
        key: "packagePlacementToleranceY",
        label: "Package Placement Tolerance Y",
        description: "Sets the tolerance along the X axis for placing a package, which also affects the silk outline. Used for parametrically generating footprints.",
        allChildNodesWillInheritThisRule: true,
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packageFabricationTolerance: {
        key: "packageFabricationTolerance",
        label: "Package Fabrication Tolerance",
        description: "Sets the tolerance for fabricating the package. Also affects the silk outline. Used for parametrically generating footprints.",
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthVector",
        validRange: [0, Infinity],
        overwritingRules: [
            "packageFabricationToleranceX",
            "packageFabricationToleranceY",
            "packageFabricationToleranceZ",
        ],
        requiresDescendantRebake: true,
    },
    packageFabricationToleranceX: {
        key: "packageFabricationToleranceX",
        label: "Package Fabrication Tolerance X",
        description: "Sets the tolerance along the X axis for fabricating the package. Also affects the silk outline. Used for parametrically generating footprints.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packageFabricationToleranceY: {
        key: "packageFabricationToleranceY",
        label: "Package Fabrication Tolerance Y",
        description: "Sets the tolerance along the Y axis for fabricating the package. Also affects the silk outline. Used for parametrically generating footprints.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    packageFabricationToleranceZ: {
        key: "packageFabricationToleranceZ",
        label: "Package Fabrication Tolerance Z",
        description: "Sets the tolerance along the Z axis for fabricating the package. Used for parametrically generating footprints.",
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    mapSilkscreenToBody: {
        key: "mapSilkscreenToBody",
        label: "Map Silkscreen to Body",
        description: "Defines how a silkscreen outline is drawn around the boundary of a footprint, indicating the space its package will occupy. Maximum will draw a rectangle which accounts for things like placement tolerance and other human error. Nominal will draw a rectangle will less tolerance. Minimum will draw a receptacle at exactly the size and location of the package. Used for parametrically generating footprints.",
        default: "maximum",
        auto_complete_options: ["maximum", "nominal", "minimum"],
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint],
        valueType: "stringOption",
        requiresDescendantRebake: true,
    },
    padOuterMargin: {
        key: "padOuterMargin",
        label: "Pad Outer Margin",
        description: "Extends the size of pads outside the perimeter of the package. Used on pads which are generated for parametric footprints, specifically QFN and QFP package families.",
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint],
        valueType: "lengthVector", // actually a single value, but we want the auto info
        auto_complete_options: [SystemPcbLayoutRulesValues.auto],
        requiresDescendantRebake: true,
    },
    thermalPadSize: {
        key: "thermalPadSize",
        label: "Thermal Pad Size",
        description: 'Sets the size of the thermal pad, sometimes called an E Pad. Used on a footprint object which is generating a parametric footprint, specifically QFN and QFP package families. Will apply to a terminal whose Pin Number property is set to "Thermal Pad", or if no such terminal is present, a virtual pad will be created. Disable the thermal pad by setting this rule\'s value to 0.',
        default: SystemPcbLayoutRulesValues.auto,
        allChildNodesWillInheritThisRule: true,
        supportedNodeTypes: [structure_1.PcbNodeTypes.footprint, structure_1.PcbNodeTypes.model],
        valueType: "lengthVector",
        validRange: [0, Infinity],
        overwritingRules: ["thermalPadSizeX", "thermalPadSizeY"],
        requiresDescendantRebake: true,
    },
    thermalPadSizeX: {
        key: "thermalPadSizeX",
        label: "Thermal Pad Size X",
        description: "Sets the size of the thermal pad along the X axis.",
        allChildNodesWillInheritThisRule: true,
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    thermalPadSizeY: {
        key: "thermalPadSizeY",
        label: "Thermal Pad Size Y",
        description: "Sets the size of the thermal pad along the Y axis.",
        allChildNodesWillInheritThisRule: true,
        valueType: "lengthValue",
        validRange: [0, Infinity],
        requiresDescendantRebake: true,
    },
    autoLayoutPriority: {
        key: "autoLayoutPriority",
        label: "Auto-Layout Priority",
        description: "Defines the order in which Auto-Layout routes a net. A value of 0 is the highest priority.",
        default: auto_layout_1.AutoLayoutPriority.GroundOrDefault,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        supportedNodeTypes: [structure_1.PcbNodeTypes.net],
        valueType: "scalar",
        validRange: [0, Infinity],
    },
    protected: {
        key: "protected",
        label: "Protected",
        description: "Marks this polygon as a protected area that auto-layout routing should avoid. When enabled, the auto-layout system will not route traces through this polygon.",
        default: false,
        setByDefault: true,
        allChildNodesWillInheritThisRule: false,
        auto_complete_options: ["true", "false"],
        supportedNodeTypes: [structure_1.PcbNodeTypes.net, structure_1.PcbNodeTypes.polygon],
        valueType: "booleanValue",
    },
};
exports.deprecatedLayoutRuleKeys = [
    "spacing",
    "spacingLeft",
    "spacingRight",
    "spacingBottom",
    "spacingTop",
    "minSpacing",
    "minSpacingLeft",
    "minSpacingRight",
    "minSpacingBottom",
    "minSpacingTop",
    "maxSpacing",
    "maxSpacingLeft",
    "maxSpacingRight",
    "maxSpacingBottom",
    "maxSpacingTop",
    "fillSpacingFromBoardEdge",
    "minFillSpacingFromBoardEdge",
    "maxFillSpacingFromBoardEdge",
];
function hasSomeLayoutRuleConfig(ruleKeys, config) {
    return ruleKeys.some((ruleKey) => exports.LayoutRules[ruleKey]?.[config]);
}
function hasSomeLayoutRuleConfigInMap(updatedNodesRulesSetsMap, config) {
    return Object.values(updatedNodesRulesSetsMap).some((node) => hasSomeLayoutRuleConfig(Object.values(node.updatedPcbLayoutRules).map((rule) => rule.key), config));
}
//# sourceMappingURL=layout.js.map